import React from "react";
import { cn } from "../../lib/cn";

export type ButtonVariant = "primary" | "ghost" | "inverse" | "soft";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  startIcon?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  loading?: boolean;
  type?: "button" | "submit";
  fullWidth?: boolean;
  /** Tailwind escape hatch for one-off overrides; prefer tokens. */
  className?: string;
  children: React.ReactNode;
}

/**
 * claudesign primary/ghost button. Mirrors `.btn` / `.btn-primary` / `.btn-ghost`
 * / `.btn-lg` from docs/redesign/ommuse-theme.css. Supersedes legacy CustomButton.
 */
export const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "md",
  startIcon,
  onClick,
  disabled,
  loading,
  type = "button",
  fullWidth,
  className,
  children,
}) => {
  const isPill = variant === "inverse" || variant === "soft";
  return (
  <button
    type={type}
    disabled={disabled || loading}
    onClick={onClick}
    className={cn(
      "inline-flex items-center justify-center gap-[9px] font-sans font-semibold whitespace-nowrap",
      isPill ? "rounded-full cursor-pointer" : "rounded-sm border border-transparent cursor-pointer",
      "transition-[background,border-color,box-shadow,transform,filter] duration-150",
      "disabled:cursor-default disabled:opacity-55",
      "[&_svg]:w-[18px] [&_svg]:h-[18px]",
      isPill
        ? size === "lg"
          ? "text-[14px] h-11 px-5"
          : size === "sm"
            ? "text-[13px] h-8 px-3.5"
            : "text-[14px] h-9 px-4"
        : size === "lg"
          ? "text-[15px] px-[22px] py-[13px]"
          : size === "sm"
            ? "text-[13px] px-[13px] py-[7px]"
            : "text-[14px] px-[17px] py-[11px]",
      variant === "primary" || variant === "inverse"
        ? "bg-accent-grad text-cyan-ink shadow-[var(--glow),inset_0_1px_0_rgba(255,255,255,.3)] hover:brightness-[1.07] hover:-translate-y-px hover:shadow-[var(--glow-strong),inset_0_1px_0_rgba(255,255,255,.32)] active:translate-y-0 active:brightness-[.98]"
        : variant === "soft"
          ? "bg-fill-2 text-hi border border-border shadow-edge-hi hover:bg-fill-3 hover:text-white"
          : "text-body border-border shadow-edge-hi bg-[linear-gradient(180deg,#1b1f28,#15181f)] hover:bg-[linear-gradient(180deg,#232732,#1a1d25)] hover:border-[#353a43] hover:text-white",
      fullWidth && "w-full",
      className,
    )}
  >
    {loading ? (
      <span
        aria-label="loading"
        className="inline-block w-[18px] h-[18px] rounded-full border-2 border-[rgba(4,34,42,.3)] border-t-[#04222a] animate-spin"
      />
    ) : (
      <>
        {startIcon}
        {children}
      </>
    )}
  </button>
  );
};

export default Button;
